import { App, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileTaskCard } from '@/components/mobile/MobileTaskCard';
import {
  PROJECT_CATEGORY_LABELS,
  PROJECT_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
} from '@/constants/project';
import { ProjectReportListSection } from '@/components/project-reports/ProjectReportListSection';
import { ROUTES } from '@/constants/routes';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import * as tasksApi from '@/services/api/tasks';
import type { ProjectDetail } from '@/types/project';
import type { TaskListItem } from '@/types/task';
import { formatDate } from '@/utils/format-date';
import { formatWanYuan } from '@/utils/format-yuan';

export function LeaderProjectDetailMobilePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const mobileVariant = location.pathname.startsWith('/m/employee') ? 'employee' : 'leader';

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [tasks, setTasks] = useState<TaskListItem[]>([]);

  const load = useCallback(async () => {
    if (!projectId) {
      return;
    }
    setLoading(true);
    try {
      const [detail, taskPage] = await Promise.all([
        projectsApi.fetchProject(projectId),
        tasksApi.fetchProjectTasks(projectId, { page: 1, pageSize: 100 }),
      ]);
      setProject(detail);
      setTasks(taskPage.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目详情失败';
      message.error(text);
      setProject(null);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [message, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!projectId) {
    return <MobileEmpty message="无效的项目" />;
  }

  if (loading) {
    return (
      <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (!project) {
    return <MobileEmpty message="项目不存在或无权查看" />;
  }

  const memberCount = project._count?.members ?? 0;

  return (
    <div className="mobile-detail-page">
      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">基本信息</span>
        </div>
        <div className="mobile-card mobile-card--flat">
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">项目编号</span>
            <span className="mobile-detail-row__value">{project.projectNo}</span>
          </div>
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">项目分类</span>
            <span className="mobile-detail-row__value">
              {project.projectCategories?.length
                ? project.projectCategories
                    .map((category) => PROJECT_CATEGORY_LABELS[category])
                    .join('、')
                : PROJECT_TYPE_LABELS[project.projectType]}
            </span>
          </div>
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">项目状态</span>
            <span className="mobile-detail-row__value">{PROJECT_STATUS_LABELS[project.status]}</span>
          </div>
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">建设单位</span>
            <span className="mobile-detail-row__value">{project.constructionUnit || '—'}</span>
          </div>
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">总投资</span>
            <span className="mobile-detail-row__value">{formatWanYuan(project.totalInvestment)}</span>
          </div>
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">项目负责人</span>
            <span className="mobile-detail-row__value">{project.projectManager?.name ?? '—'}</span>
          </div>
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">项目周期</span>
            <span className="mobile-detail-row__value">
              {formatDate(project.startDate)} ~ {formatDate(project.endDate)}
            </span>
          </div>
          {project.summary ? (
            <div className="mobile-detail-text">
              <div className="mobile-detail-row__label">项目概况</div>
              <p>{project.summary}</p>
            </div>
          ) : null}
          {project.annualPlan ? (
            <div className="mobile-detail-text">
              <div className="mobile-detail-row__label">
                {project.annualPlanYear ?? new Date().getFullYear()}年推进计划
              </div>
              <p>{project.annualPlan}</p>
            </div>
          ) : null}
          {project.remarks ? (
            <div className="mobile-detail-text">
              <div className="mobile-detail-row__label">备注</div>
              <p>{project.remarks}</p>
            </div>
          ) : null}
        </div>
      </section>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">项目成员</span>
          <span className="mobile-section__meta">{memberCount} 人</span>
        </div>
        <div className="mobile-card mobile-card--flat">
          <div className="mobile-leader-card">
            <div className="mobile-leader-card__name">{project.projectManager?.name ?? '—'}</div>
            <div className="mobile-leader-card__title">项目负责人</div>
          </div>
          {memberCount > 1 ? (
            <p className="mobile-hint">本项目共 {memberCount} 名成员（含负责人）</p>
          ) : null}
        </div>
      </section>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">项目任务</span>
          <span className="mobile-section__meta">{tasks.length} 项</span>
        </div>
        {tasks.length === 0 ? (
          <MobileEmpty message="暂无任务" />
        ) : (
          tasks.map((task) => (
            <MobileTaskCard
              key={task.id}
              task={task}
              onClick={() => {
                if (mobileVariant === 'employee') {
                  navigate(ROUTES.mobile.employee.taskDetail(task.id));
                  return;
                }
                navigate(ROUTES.mobile.leader.projectTaskDetail(projectId, task.id));
              }}
            />
          ))
        )}
      </section>

      <ProjectReportListSection
        projectId={projectId}
        project={project}
        onOpenReport={(reportId) =>
          navigate(
            mobileVariant === 'employee'
              ? ROUTES.mobile.employee.projectReportDetail(projectId, reportId)
              : ROUTES.mobile.leader.projectReportDetail(projectId, reportId),
          )
        }
        onGoDailyReport={() =>
          navigate(
            mobileVariant === 'employee'
              ? ROUTES.mobile.employee.reports
              : ROUTES.mobile.leader.myReport,
          )
        }
      />
    </div>
  );
}
